import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import TopBar from "@/components/TopBar";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const userData = await login(email, password);
      const dest = {
        operations: "/admin/operations",
        logistics:  "/admin/logistics",
        commercial: "/admin/commercial",
        finance:    "/admin/finance",
        hr:         "/admin/hr",
      }[userData?.role] || "/admin";
      navigate(dest);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9FB] milk-grain">
      <TopBar />
      <div className="max-w-md mx-auto px-5 pt-4 pb-24">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          data-testid="login-back-btn"
          className="gap-2 -ml-3 text-[#5C4664] hover:bg-[#FCE7F3]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("common.home")}
        </Button>
        <div className="mt-12">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#D81B8C] mb-3">
            {t("common.appName")}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#2A0E3C] leading-tight">
            {t("admin.login")}
          </h1>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-5">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
              {t("admin.email")}
            </Label>
            <Input
              type="email"
              data-testid="login-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="mt-1.5 h-12 rounded-xl border-[#F2E2EC] focus-visible:ring-[#D81B8C]"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
              {t("admin.password")}
            </Label>
            <Input
              type="password"
              data-testid="login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="mt-1.5 h-12 rounded-xl border-[#F2E2EC] focus-visible:ring-[#D81B8C]"
            />
          </div>
          {error && (
            <p data-testid="login-error" className="text-sm text-[#D81B8C] font-medium">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white h-14 font-semibold gap-2 active:scale-95 transition-transform"
          >
            {loading ? t("common.loading") : t("admin.signIn")}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
